import React, { useCallback, useEffect, useState } from "react";
import { Button, ColorPicker, Empty, Modal, Segmented, Slider, Space, Tooltip, Typography, Upload, message } from "antd";
import { Content } from "antd/es/layout/layout";
import { DeleteOutlined, UploadOutlined } from "@ant-design/icons";
import { carregarFundo, excluirFundo, listarFundos, prepararFundo, salvarFundo } from "./api";

const { Text } = Typography;

/**
 * Banco de imagens de fundo dos gráficos.
 *
 * Cada fundo é guardado como data URI já reduzido a 604x366. Isso não é
 * economia de espaço por acaso: a imagem precisa entrar EMBUTIDA no SVG,
 * porque um SVG rasterizado dentro de <img> — que é como a exportação
 * funciona — não carrega recurso externo e ainda contaminaria o canvas,
 * fazendo a geração do JPEG falhar.
 *
 * Sobre a imagem vai um véu de cor sólida com opacidade regulável, que é o
 * que mantém o texto legível sobre qualquer foto.
 */
export default function PainelFundo({ spec, onChange }) {
  const [fundos, setFundos] = useState([]);
  const [miniaturas, setMiniaturas] = useState({});
  const [carregando, setCarregando] = useState(false);
  const fundoAtual = spec.fundoImagem || {};

  const recarregar = useCallback(async () => {
    try {
      const lista = await listarFundos();
      setFundos(lista);
      // As miniaturas vêm sob demanda: a listagem devolve só metadados
      // para não trafegar todas as imagens de uma vez.
      const faltando = lista.filter((f) => !miniaturas[f.codigo]);
      if (faltando.length) {
        const carregadas = await Promise.all(
          faltando.map(async (f) => [f.codigo, await carregarFundo(f.codigo)])
        );
        setMiniaturas((anterior) => ({ ...anterior, ...Object.fromEntries(carregadas) }));
      }
    } catch (err) {
      console.error("Erro ao listar fundos:", err);
    }
    // miniaturas fora das deps de propósito: só serve como cache aqui, e
    // incluí-la faria o efeito se repetir a cada carregamento.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    recarregar();
  }, [recarregar]);

  const aoEnviar = async (arquivo) => {
    setCarregando(true);
    try {
      const dataUri = await prepararFundo(arquivo);
      const nome = arquivo.name.replace(/\.[^.]+$/, "").slice(0, 110);
      await salvarFundo(nome, dataUri);
      message.success("Fundo adicionado ao banco.");
      await recarregar();
    } catch (err) {
      message.error(err.message || "Não foi possível enviar a imagem.");
    } finally {
      setCarregando(false);
    }
    return false; // impede o upload automático do antd
  };

  const escolher = (codigo) => {
    const dataUri = miniaturas[codigo];
    if (!dataUri) return;
    onChange({
      fundoImagem: {
        codigo,
        dataUri,
        cor: fundoAtual.cor || "#000000",
        opacidade: fundoAtual.opacidade ?? 0.45,
        abrangencia: fundoAtual.abrangencia ?? "tudo",
      },
    });
  };

  const remover = () => onChange({ fundoImagem: null });

  const apagar = (codigo, nome) => {
    Modal.confirm({
      title: "Excluir fundo",
      content: `Remover "${nome}" do banco de fundos? Gráficos já gerados não mudam.`,
      okText: "Excluir",
      okButtonProps: { danger: true },
      cancelText: "Cancelar",
      onOk: async () => {
        await excluirFundo(codigo);
        if (fundoAtual.codigo === codigo) remover();
        await recarregar();
      },
    });
  };

  return (
    <Content style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <Space wrap>
        <Upload beforeUpload={aoEnviar} showUploadList={false} accept="image/*">
          <Button size="small" icon={<UploadOutlined />} loading={carregando}>
            Adicionar imagem
          </Button>
        </Upload>
        {fundoAtual.dataUri && (
          <Button size="small" onClick={remover}>
            Sem fundo
          </Button>
        )}
      </Space>

      {fundos.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<Text type="secondary">Nenhum fundo no banco ainda</Text>}
        />
      ) : (
        <Content
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))",
            gap: 8,
          }}
        >
          {fundos.map((fundo) => {
            const ativo = fundoAtual.codigo === fundo.codigo;
            return (
              <Content
                key={fundo.codigo}
                onClick={() => escolher(fundo.codigo)}
                style={{
                  position: "relative",
                  cursor: "pointer",
                  borderRadius: 6,
                  overflow: "hidden",
                  outline: ativo ? "3px solid #1d5c9d" : "1px solid #ddd",
                  aspectRatio: "604 / 366",
                  background: "#eee",
                }}
              >
                {miniaturas[fundo.codigo] && (
                  <img
                    src={miniaturas[fundo.codigo]}
                    alt={fundo.nome}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                )}
                <Tooltip title="Excluir do banco">
                  <DeleteOutlined
                    onClick={(e) => {
                      e.stopPropagation();
                      apagar(fundo.codigo, fundo.nome);
                    }}
                    style={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      color: "#fff",
                      background: "rgba(0,0,0,0.55)",
                      borderRadius: 4,
                      padding: 3,
                    }}
                  />
                </Tooltip>
              </Content>
            );
          })}
        </Content>
      )}

      {fundoAtual.dataUri && (
        <Content style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <Content style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Text style={{ fontSize: 12, width: 108 }}>Preenchimento</Text>
            <Segmented
              size="small"
              value={fundoAtual.abrangencia ?? "tudo"}
              onChange={(v) =>
                onChange({ fundoImagem: { ...fundoAtual, abrangencia: v } })
              }
              options={[
                { value: "tudo", label: "Tudo" },
                { value: "grafico", label: "Só o gráfico" },
              ]}
            />
          </Content>
          <Content style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Text style={{ fontSize: 12, width: 108 }}>Cor do véu</Text>
            <ColorPicker
              size="small"
              value={fundoAtual.cor || "#000000"}
              disabledAlpha
              onChange={(c) =>
                onChange({ fundoImagem: { ...fundoAtual, cor: c.toHexString() } })
              }
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              cor sólida sobre a foto
            </Text>
          </Content>
          <Content style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Text style={{ fontSize: 12, width: 108 }}>Opacidade</Text>
            <Slider
              style={{ flex: 1 }}
              min={0}
              max={1}
              step={0.05}
              value={fundoAtual.opacidade ?? 0.45}
              onChange={(v) => onChange({ fundoImagem: { ...fundoAtual, opacidade: v } })}
            />
          </Content>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Aumente a opacidade até o texto ficar legível sobre a imagem.
          </Text>
        </Content>
      )}
    </Content>
  );
}
